import { Bar, BarChart as RBarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import Box from "@mui/material/Box";
import ChartLegend from "./ChartLegend";
import { SERIES_COLORS } from "./palette";

/** Uma faixa do histograma: intervalo [from, to) no eixo numérico; `to = null` = faixa aberta ("90+"). */
export type HistogramBin = { from: number; to: number | null; label: string; [serie: string]: number | string | null };
export type HistogramSeries = { key: string; label: string; color?: string };
/** Linha vertical de referência no valor `x` do eixo (ex.: fronteira de faixa em 30 dias). */
export type HistogramReference = { x: number; label: string; color?: string };
export type HistogramChartProps = {
  bins: HistogramBin[];
  series: HistogramSeries[];
  ariaLabel: string;
  /** Largura de cada faixa (no eixo); a faixa aberta usa a mesma largura. */
  binWidth: number;
  references?: HistogramReference[];
  /** Rótulo do eixo X para um valor (ex.: 30 → "30 d"). */
  tickFormatter?: (v: number) => string;
  ticks?: number[];
  height?: number;
  /** Só para teste (jsdom mede 0 no ResponsiveContainer). */
  width?: number;
};

const REF_COLOR = "#64748b";

/** Centro de cada faixa no eixo numérico: as referências caem exatamente nas fronteiras. */
export const histogramData = (bins: HistogramBin[], binWidth: number) =>
  bins.map((b) => ({ ...b, x: b.from + binWidth / 2 }));

/**
 * Histogramas sobrepostos (séries lado a lado, um pouco transparentes) num eixo X numérico,
 * com linhas de referência e legenda em HTML. Cada barra cobre [from, to) da faixa.
 */
export default function HistogramChart({ bins, series, ariaLabel, binWidth, references = [], tickFormatter, ticks, height = 260, width }: HistogramChartProps) {
  const data = histogramData(bins, binWidth);
  const last = bins.length ? bins[bins.length - 1] : null;
  const max = last ? (last.to ?? last.from + binWidth) : binWidth;
  const labelOf = new Map(data.map((d) => [d.x, d.label]));
  const color = (s: HistogramSeries, i: number) => s.color ?? SERIES_COLORS[i % SERIES_COLORS.length];
  const chart = (
    <RBarChart data={data} width={width} height={height} barGap={0} barCategoryGap="12%" margin={{ top: 20, right: 16, bottom: 4, left: 8 }}>
      <CartesianGrid strokeDasharray="3 3" vertical={false} />
      <XAxis dataKey="x" type="number" domain={[0, max]} ticks={ticks} tickFormatter={tickFormatter} allowDecimals={false} />
      <YAxis allowDecimals={false} />
      <Tooltip labelFormatter={(x: number) => labelOf.get(x) ?? String(x)} />
      {series.map((s, i) => (
        <Bar key={s.key} dataKey={s.key} name={s.label} fill={color(s, i)} fillOpacity={0.8} isAnimationActive={false} />
      ))}
      {references.map((r) => (
        <ReferenceLine key={r.x} x={r.x} stroke={r.color ?? REF_COLOR} strokeWidth={1.5} strokeDasharray="4 3"
          label={{ value: r.label, position: "top", fill: r.color ?? REF_COLOR, fontSize: 11, fontWeight: 700 }} />
      ))}
    </RBarChart>
  );
  return (
    <Box role="img" aria-label={ariaLabel}>
      {width ? chart : <ResponsiveContainer width="100%" height={height}>{chart}</ResponsiveContainer>}
      <ChartLegend items={series.map((s, i) => ({ label: s.label, color: color(s, i), kind: "bar" as const }))} />
    </Box>
  );
}
